import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createVNode as _createVNode, createBlock as _createBlock, createTextVNode as _createTextVNode, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["aria-busy"]
const _hoisted_2 = {
  key: 0,
  role: "status"
}
const _hoisted_3 = {
  key: 1,
  role: "alert"
}
const _hoisted_4 = { key: 2 }
const _hoisted_5 = {
  key: 0,
  role: "status"
}
const _hoisted_6 = {
  key: 1,
  role: "alert"
}
const _hoisted_7 = {
  key: 2,
  role: "status"
}
const _hoisted_8 = {
  key: 4,
  class: "bsp-component-actions"
}
const _hoisted_9 = { key: 6 }
const _hoisted_10 = {
  key: 7,
  role: "status"
}

import { computed, onScopeDispose, ref, watch } from 'vue'
import { AButton, ASelector, ASwitch, ATag } from '@adereso-ai/vue-components'
import AccountService from '@/services/AccountService/AccountService'
import MetaService from '@/services/MetaService/MetaService'
import type { PhoneNumber } from '@/services/AccountService/types'
import { useCatalogVisibility } from '@/composables/useCatalogVisibility'


export default /*@__PURE__*/_defineComponent({
  __name: 'CatalogVisibilitySettings',
  props: {
    wabaId: {},
    canManage: { type: Boolean },
    disabled: { type: Boolean, default: false },
    initialPhoneId: { default: '' }
  },
  emits: ["update:phoneId"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const emit = __emit
const selectedPhoneId = ref('')
const phones = ref<PhoneNumber[]>([])
const phonesLoading = ref(false)
const phonesError = ref('')
const saved = ref(false)
let phoneGeneration = 0
const phoneChoices = computed(() => phones.value.map(phone => ({
  title: `${phone.display_phone_number} · ${phone.verified_name}`, value: phone.id
})))
const { visible, loading, saving, error, refresh, save } = useCatalogVisibility(
  computed(() => props.wabaId), selectedPhoneId, MetaService.getInstance
)

async function loadPhones () {
  const request = ++phoneGeneration
  selectedPhoneId.value = ''
  phones.value = []
  phonesError.value = ''
  phonesLoading.value = true
  try {
    const wabaId = props.wabaId
    const result = await (await AccountService.getInstance()).getWhatsappBusinessAccountPhoneNumbers(wabaId)
    if (request !== phoneGeneration) return
    const meta = await MetaService.getInstance()
    // Local records can outlive their Meta phone asset. Only offer numbers for
    // which Meta can actually read catalog visibility; never guess from order.
    const checks = await Promise.allSettled(result.map(phone => meta.getCommerceSettings(wabaId, phone.id)))
    if (request !== phoneGeneration) return
    phones.value = result.filter((phone, index) => checks[index].status === 'fulfilled')
    if (result.length && !phones.value.length) {
      throw new Error('Meta could not confirm catalog access for any phone number. Refresh to try again.')
    }
    selectedPhoneId.value = phones.value.some(phone => phone.id === props.initialPhoneId)
      ? props.initialPhoneId
      : phones.value[0]?.id || ''
  } catch (e) {
    if (request === phoneGeneration) phonesError.value = e instanceof Error ? e.message : String(e)
  } finally {
    if (request === phoneGeneration) phonesLoading.value = false
  }
}

async function setVisibility (value: unknown) {
  if (typeof value !== 'boolean' || !props.canManage || props.disabled) return
  saved.value = false
  saved.value = await save(value)
}

async function refreshSettings () {
  saved.value = false
  if (phonesError.value || !phones.value.length) await loadPhones()
  else await refresh()
}

watch(() => props.wabaId, loadPhones, { immediate: true })
watch(selectedPhoneId, id => {
  saved.value = false
  if (id) emit('update:phoneId', id)
})
watch(() => props.initialPhoneId, id => {
  if (phones.value.some(phone => phone.id === id)) selectedPhoneId.value = id
})
onScopeDispose(() => { phoneGeneration++ })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "bsp-catalog-visibility",
    "aria-busy": phonesLoading.value || _unref(loading) || _unref(saving)
  }, [
    _cache[6] || (_cache[6] = _createElementVNode("p", null, "Visibility is configured separately for each WhatsApp number. Hiding the catalog prevents catalog template messages from being sent from that number.", -1)),
    (phonesLoading.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_2, "Loading WhatsApp numbers…"))
      : (phonesError.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_3, _toDisplayString(phonesError.value), 1))
        : (!phones.value.length)
          ? (_openBlock(), _createElementBlock("p", _hoisted_4, "No phone numbers are available for this WABA."))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
              _createVNode(_unref(ASelector), {
                modelValue: selectedPhoneId.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedPhoneId).value = $event)),
                items: phoneChoices.value,
                "item-title": "title",
                "item-value": "value",
                label: "WhatsApp phone number",
                disabled: _unref(saving) || __props.disabled
              }, null, 8, ["modelValue", "items", "disabled"]),
              (_unref(loading))
                ? (_openBlock(), _createElementBlock("p", _hoisted_5, "Checking catalog visibility in Meta…"))
                : (_unref(error))
                  ? (_openBlock(), _createElementBlock("p", _hoisted_6, "Could not confirm catalog visibility: " + _toDisplayString(_unref(error)) + ". Refresh before making changes.", 1))
                  : (_unref(visible) === null)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_7, "Meta has not returned visibility settings for this number. Choose whether to show or hide the catalog; Meta must confirm the change after saving."))
                    : (_unref(visible) !== undefined)
                      ? (_openBlock(), _createBlock(_unref(ATag), {
                          key: 3,
                          text: _unref(visible) ? 'Visible on this number' : 'Hidden on this number',
                          color: _unref(visible) ? 'success' : 'warning',
                          type: "secondary"
                        }, null, 8, ["text", "color"]))
                      : _createCommentVNode("", true),
              (_unref(visible) === null)
                ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                    _createVNode(_unref(AButton), {
                      size: "small",
                      loading: _unref(saving),
                      disabled: !__props.canManage || __props.disabled || _unref(loading) || _unref(saving),
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (setVisibility(true)))
                    }, {
                      default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                        _createTextVNode("Show catalog", -1)
                      ]))]),
                      _: 1
                    }, 8, ["loading", "disabled"]),
                    _createVNode(_unref(AButton), {
                      size: "small",
                      variant: "outlined",
                      loading: _unref(saving),
                      disabled: !__props.canManage || __props.disabled || _unref(loading) || _unref(saving),
                      onClick: _cache[2] || (_cache[2] = ($event: any) => (setVisibility(false)))
                    }, {
                      default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                        _createTextVNode("Hide catalog", -1)
                      ]))]),
                      _: 1
                    }, 8, ["loading", "disabled"])
                  ]))
                : (_openBlock(), _createBlock(_unref(ASwitch), {
                    key: 5,
                    "model-value": _unref(visible) === true,
                    label: "Show catalog in WhatsApp on this number",
                    loading: _unref(saving),
                    disabled: !__props.canManage || __props.disabled || _unref(loading) || _unref(saving) || _unref(visible) === undefined,
                    "onUpdate:modelValue": setVisibility
                  }, null, 8, ["model-value", "loading", "disabled"])),
              (!__props.canManage)
                ? (_openBlock(), _createElementBlock("p", _hoisted_9, "Only BSP administrators can change catalog visibility."))
                : _createCommentVNode("", true),
              (saved.value)
                ? (_openBlock(), _createElementBlock("p", _hoisted_10, "Catalog visibility confirmed in Meta."))
                : _createCommentVNode("", true)
            ], 64)),
    _createVNode(_unref(AButton), {
      size: "small",
      variant: "outlined",
      "prepend-icon": "tabler:refresh",
      disabled: _unref(loading) || _unref(saving) || phonesLoading.value || __props.disabled,
      onClick: refreshSettings
    }, {
      default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
        _createTextVNode("Refresh visibility", -1)
      ]))]),
      _: 1
    }, 8, ["disabled"])
  ], 8, _hoisted_1))
}
}

})